import type { Chart, ChartOptions } from 'chart.js';
import commaNumber from 'comma-number';
import { type MouseEvent, useMemo, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import styled from 'styled-components';

import { CenterFlex, VFlex } from '@/components/base';
import Spinner from '@/components/Common/Spinner';
import Transition from '@/components/Common/Transition';
import { CHART_FREQUENCY } from '@/entities/chart/model/constants';
import chartUtils, {
  parseDateFormatByFrequency,
} from '@/features/(charts)/chart-renderer/lib/chart-utils';
import { CollapseState } from '@/features/AskSyncly/types/constants';
import type { FeedbackBlockListModalOptions } from '@/features/Feedback/components/FeedbackListModal/types';
import SearchDrilldownMenu from '@/features/GlobalSearch/components/ResultContents/SearchDrilldownMenu';
import { useSearchDetailContext } from '@/features/GlobalSearch/context/SearchDetailContext';
import useGlobalSearchModalAction from '@/features/GlobalSearch/hooks/useGlobalSearchModalAction';
import useColor from '@/hooks/domain/useColor';
import useModalState from '@/hooks/pure/useModalState';
import service from '@/services/index';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import { GPT_DOMAIN } from '@/types/gptConst';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

type Props = {
  isLoading: boolean;
  feedbackTrend?: StatisticDataT;
  frequency: CHART_FREQUENCY;
};

const FeedbackVolumeDisplay = (props: Props) => {
  const { toDetailPage, refreshHistoryList } = useGlobalSearchModalAction();
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const { searchResult, dateRange } = useSearchDetailContext();
  const selectedArea = useRef<[number, number, number, number]>([0, 0, 0, 0]);
  const [selectedChartBar, setSelectedChartBar] =
    useState<SelectedChartBar>(null);
  const color = useColor();
  const chartRef = useRef<Chart<'bar'>>(null);
  const { status, open, close, pos, setPos, setModalState } =
    useModalState<SelectedChartBar>(null);

  const trendChartData = useMemo(() => {
    const format = chartUtils.getFormatByFrequency(props.frequency);
    return {
      labels: props.feedbackTrend?.buckets.map((bucket) =>
        format(getTimezoneDayjs(bucket.bucket as string)),
      ),
      datasets: [
        {
          label: 'Feedback',
          data: props.feedbackTrend?.buckets.map((bucket) => bucket.count),
          backgroundColor: color.primary100,
          borderRadius: 4,
          order: 1,
          yAxisID: 'y1',
        },
      ],
    };
  }, [color.primary100, props.feedbackTrend?.buckets, props.frequency]);

  const backgroundChartArea = useMemo(() => {
    return {
      id: `search_feedback_trend_backgroundChartArea`,
      beforeDatasetsDraw(chart: Chart) {
        chart.ctx.fillStyle = color.ND08;
        chart.ctx.fillRect(...selectedArea.current);
      },
    };
  }, [color.ND08]);

  const barChartOptions: ChartOptions<'bar'> = useMemo(() => {
    const options: ChartOptions<'bar'> = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          mode: 'nearest',
          intersect: false,
          enabled: true,
        },
        datalabels: {
          display: false,
        },
        backgroundChartArea: backgroundChartArea,
      },
      scales: {
        x: {
          ticks: {
            autoSkip: true,
            maxTicksLimit: 15,
          },
          grid: {
            display: false,
          },
        },
        y1: {
          grace: '10%',
          beginAtZero: true,
          ticks: {
            autoSkip: true,
            maxTicksLimit: 5,
            callback: (value) => {
              if (Math.floor(Number(value)) === Number(value)) {
                return commaNumber(value);
              }
            },
          },
        },
      },
      onResize(chart) {
        if (!selectedChartBar) return;
        if (!chart.chartArea) return;
        const meta = chart.getDatasetMeta(selectedChartBar.datasetIndex);
        const element = meta.data[selectedChartBar.dataIndex];
        if (!element) return;
        const { top, height } = chart.chartArea;
        const { x, width } = element as unknown as {
          x: number;
          width: number;
        };
        selectedArea.current = [
          x - width / 2 - width / 5,
          top,
          width * 1.4,
          height,
        ];
      },
      onClick: (e: any) => {
        const el = chartRef.current?.getElementsAtEventForMode(
          e,
          'nearest',
          { intersect: false },
          true,
        )[0];

        if (!el || selectedChartBar?.dataIndex === el.index) {
          selectedArea.current = [0, 0, 0, 0];
          setSelectedChartBar(null);
          return;
        }
        const chart: Chart = e.chart;
        const len = chart.data.labels?.length || 0;
        const { top, height, width } = chart.chartArea;
        const { x } = el.element;
        const areaWidth = width / len;
        selectedArea.current = [x - areaWidth / 2, top, areaWidth, height];

        const val: SelectedChartBar = {
          dataIndex: el.index,
          datasetIndex: el.datasetIndex,
        };

        setPos({
          top: e.native.clientY,
          left: e.native.clientX,
        });
        setModalState(val);
        open();
        setSelectedChartBar(val);
      },
    };
    return options;
  }, [backgroundChartArea, open, selectedChartBar, setModalState, setPos]);

  const onAddToFilterHandler = async (e: MouseEvent) => {
    e.stopPropagation();
    close();
    if (!trendChartData || !selectedChartBar) return;
    const label = trendChartData.labels?.[selectedChartBar.dataIndex];
    if (!label) return;
    const parsedDateRange = parseDateFormatByFrequency(label, props.frequency);

    const start_date = parsedDateRange.start;
    const end_date = parsedDateRange.end;

    const res = await service.globalSearch.submitSearch({
      title: `${searchResult.title} copy`,
      query: searchResult.query,
      filter: {
        ...searchResult.filter,
        [FEEDBACK_COLUMNS.DATE_START]: start_date,
        [FEEDBACK_COLUMNS.DATE_END]: end_date,
      },
      options: {
        trend_frequency: CHART_FREQUENCY.DAY,
      },
    });

    toDetailPage(res.alias);
    refreshHistoryList();
  };

  const onViewFeedbackListHandler = async (
    e: MouseEvent,
    initialOpen?: boolean,
  ) => {
    e.stopPropagation();
    close();
    if (!trendChartData || !selectedChartBar) return;
    const label = trendChartData.labels?.[selectedChartBar.dataIndex];
    if (!label) return;
    const parsedDateRange = parseDateFormatByFrequency(label, props.frequency);
    const [start, end] = dateRange;
    const start_date = start.isBefore(getTimezoneDayjs(parsedDateRange.start))
      ? parsedDateRange.start
      : start.toJSON();
    const end_date = end.isAfter(getTimezoneDayjs(parsedDateRange.end))
      ? parsedDateRange.end
      : end.toJSON();

    const filterPayload = {
      [FEEDBACK_COLUMNS.DATE_START]: getTimezoneDayjs(start_date).toJSON(),
      [FEEDBACK_COLUMNS.DATE_END]: getTimezoneDayjs(end_date).toJSON(),
    };

    setNewModal<FeedbackBlockListModalOptions>({
      alias: searchResult.alias,
      type: DETAIL_MODAL_TYPE.FEEDBACK_BLOCK_LIST,
      idx: 0,
      keys: [start_date, end_date],
      options: {
        queryKeys: [start_date, end_date],
        title: 'Feedback list',
        subTitle: `${getTimezoneDayjs(start_date).format(
          DATE_FORMAT.readable,
        )} - ${getTimezoneDayjs(end_date).format(DATE_FORMAT.readable)}`,
        domain: 'global-search',
        fetcher: async (sortStatus, pageParam) => {
          const res = await service.globalSearch.fetchSearchFeedbackList(
            searchResult.alias,
            {
              query: {
                cursor: pageParam,
                sortby: sortStatus.sortBy,
                order: sortStatus.orderStatus,
              },
              payload: filterPayload,
            },
          );
          const PAGE_SIZE = 50;
          return {
            count: res.count,
            list: res.feedbacks,
            paginate_token: res.feedbacks[PAGE_SIZE]?.alias,
          };
        },
        exportFetcher: async (reason?: string, password?: string) => {
          await service.globalSearch.exportSearchFeedbackList({
            alias: searchResult.alias,
            filterPayload: filterPayload,
            reason: reason,
            password: password,
          });
        },
        socketInfo: {
          domain: GPT_DOMAIN.GLOBAL_SEARCH,
          search: {
            alias: searchResult.alias,
            filterPayload: filterPayload,
          },
          initialState: initialOpen ? CollapseState.EXPANDED : undefined,
        },
      },
    });
  };

  return (
    <Style g={24} f={1}>
      <Transition
        status={!props.isLoading}
        style={{
          flex: 1,
          cursor: 'pointer',
        }}
        fallback={
          <CenterFlex height="100%">
            <Spinner />
          </CenterFlex>
        }
      >
        <Bar
          key="search-feedback-trend-chart"
          data={trendChartData}
          options={barChartOptions}
          ref={chartRef}
          plugins={[backgroundChartArea]}
        />
      </Transition>
      <SearchDrilldownMenu
        pos={{
          top: pos?.top as number,
          left: pos?.left as number,
        }}
        status={status}
        close={close}
        onViewFeedbackListClick={(e) => onViewFeedbackListHandler(e)}
        onAddToFilterClick={onAddToFilterHandler}
        onAskSynclyAIClick={(e) => onViewFeedbackListHandler(e, true)}
      />
    </Style>
  );
};

const Style = styled(VFlex)`
  height: 100%;
`;

export default FeedbackVolumeDisplay;
